import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';

export type TSingleCase = {
  caseId: string;
  name: string;
  createdAt: string;
  description: string;
  notes: string;
  status?: 'running' | 'resolved' | 'closed';
  userId?: string;
  conversationId?: string;
  labelCases: {
    labelCaseId: string;
    name: string;
    color: string;
  }[];
  tickets: {
    ticketId: string;
    title: string;
    content: string;
    spaceColumn: {
      spaceColumnId: string;
      name: string;
      space: {
        spaceId: string;
        name: string;
      };
    };
  }[];
  customer: {
    customerId: string;
    name: string;
    profilePicture?: string;
    email?: string;
    phoneNumber?: string;
    isGroup: boolean;
    groupInfo: {
      groupInfoId: string;
      name: string;
      picture: string;
    } | null;
    labelCustomers: {
      labelCustomerId: string;
      name: string;
      color: string;
    }[];
  };
  organizationChannel: {
    organizationChannelId: string;
    displayName: string;
    channel: {
      channelId: string;
      name: string;
    };
  };
  lastMessage: {
    messageId: string;
    content: string;
    createdAt: string; // ISO date string
  };
  agentList: {
    userId: string;
    fullname: string;
    profileImage?: string;
  }[];
};

export type TSingleCaseResponse = ApiResponse<TSingleCase>;
export type TSingleCaseParams = {
  caseId?: string;
};

export const singleCase = async (params: TSingleCaseParams): Promise<TSingleCaseResponse> => {
  const url = `/case/${params.caseId}`;

  return axiosInstance.get(url, { params });
};

export const SINGLE_CASE_QUERY_KEY = 'single-case-query';

export const singleCaseQueryOptions = <TData = TSingleCaseResponse>(
  params: TSingleCaseParams,
  select?: (data: TSingleCaseResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      SINGLE_CASE_QUERY_KEY,
      {
        caseId: params?.caseId,
      },
    ],
    queryFn: () => singleCase(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useSingleCaseQuery<TData>(options: {
  params: TSingleCaseParams;
  queryConfig?: QueryConfig<typeof singleCaseQueryOptions<TData>>;
  select: (data: TSingleCaseResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useSingleCaseQuery(options: {
  params: TSingleCaseParams;
  queryConfig?: QueryConfig<typeof singleCaseQueryOptions<TSingleCaseResponse>>;
}): ReturnType<typeof useQuery<TSingleCaseResponse>>;

// Implementation
export function useSingleCaseQuery<TData = TSingleCaseResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TSingleCaseParams;
  queryConfig?: QueryConfig<typeof singleCaseQueryOptions<TData | TSingleCaseResponse>>;
  select?: (data: TSingleCaseResponse) => TData;
}) {
  return useQuery({
    ...singleCaseQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TSingleCaseResponse ? TSingleCaseResponse : TData>>;
}
